import React, { createContext, useState, ReactNode } from 'react';

interface DrawerContextType {
  eventOpen: boolean;
  commentMessageOpen: boolean;
  processDrawerOpen: boolean;
  auditDrawerOpen: boolean;
  linkRecordSidebarOpen: boolean;
  setEventOpen: (open: boolean) => void;
  setCommentMessageOpen: (open: boolean) => void;
  setProcessDrawerOpen: (open: boolean) => void;
  setAuditDrawerOpen: (open: boolean) => void;
  setLinkRecordSidebarOpen: (open: boolean) => void;
  closeAllDrawers: () => void;
}

export const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function DrawerContextProvider({ children }: { children: ReactNode }) {
  const [eventOpen, setEventOpen] = useState(false);
  const [commentMessageOpen, setCommentMessageOpen] = useState(false);
  const [processDrawerOpen, setProcessDrawerOpen] = useState(false);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [linkRecordSidebarOpen, setLinkRecordSidebarOpen] = useState(false);

  const closeAllDrawers = () => {
    setEventOpen(false);
    setCommentMessageOpen(false);
    setProcessDrawerOpen(false);
    setAuditDrawerOpen(false);
    setLinkRecordSidebarOpen(false);
  };

  // Custom setters that close all other drawers before opening the requested one
  const handleSetEventOpen = (open: boolean) => {
    if (open) {
      closeAllDrawers();
      setEventOpen(true);
    } else {
      setEventOpen(false);
    }
  };

  const handleSetCommentMessageOpen = (open: boolean) => {
    if (open) {
      closeAllDrawers();
      setCommentMessageOpen(true);
    } else {
      setCommentMessageOpen(false);
    }
  };

  const handleSetProcessDrawerOpen = (open: boolean) => {
    if (open) {
      closeAllDrawers();
      setProcessDrawerOpen(true);
    } else {
      setProcessDrawerOpen(false);
    }
  };

  const handleSetAuditDrawerOpen = (open: boolean) => {
    if (open) {
      closeAllDrawers();
      setAuditDrawerOpen(true);
    } else {
      setAuditDrawerOpen(false);
    }
  };

  const handleSetLinkRecordSidebarOpen = (open: boolean) => {
    if (open) {
      closeAllDrawers();  
      setLinkRecordSidebarOpen(true);
    } else {
      setLinkRecordSidebarOpen(false);
    }
  };

  const value: DrawerContextType = {
    eventOpen,
    commentMessageOpen,
    processDrawerOpen,
    auditDrawerOpen,
    linkRecordSidebarOpen,
    setEventOpen: handleSetEventOpen,
    setCommentMessageOpen: handleSetCommentMessageOpen,
    setProcessDrawerOpen: handleSetProcessDrawerOpen,
    setAuditDrawerOpen: handleSetAuditDrawerOpen,
    setLinkRecordSidebarOpen: handleSetLinkRecordSidebarOpen,
    closeAllDrawers,
  };

  return (
    <DrawerContext.Provider value={value}>
      {children}
    </DrawerContext.Provider>
  );
}

export function useDrawerContext() {
  const context = React.useContext(DrawerContext);
  if (context === undefined) {
    throw new Error('useDrawerContext must be used within DrawerContextProvider');
  }
  return context;
}
